import type { RecurlyError } from '@recurly/recurly-js';
import logThreeDSecureActionRequired from '@alltrails/amplitude/events/logThreeDSecureActionRequired';
import logThreeDSecureAuthCompleted from '@alltrails/amplitude/events/logThreeDSecureAuthCompleted';
import logThreeDSecureChallengeFailed from '@alltrails/amplitude/events/logThreeDSecureChallengeFailed';
import runThreeDSecureChallenge from './runThreeDSecureChallenge';

function isRecurlyError(error: unknown): error is RecurlyError {
  return typeof error === 'object' && error !== null && 'message' in error && 'code' in error;
}

export type ThreeDSecureAnalyticsInfo = {
  payment_method?: string;
  product_id?: string;
  purchase_screen_type_plan?: string;
  purchase_type?: string;
  upsell_trigger?: string;
};

type RunThreeDSecureFlowArgs<T> = {
  actionTokenId: string;
  analyticsInfo: ThreeDSecureAnalyticsInfo;
  checkoutSessionId: string;
  completeCheckoutSession: (resultTokenId: string) => Promise<T>;
  container: HTMLElement;
  onThreeDSEnd: () => void;
  onThreeDSStart: () => void;
};

export default async function runThreeDSecureFlow<T>({
  actionTokenId,
  analyticsInfo,
  checkoutSessionId,
  completeCheckoutSession,
  container,
  onThreeDSEnd,
  onThreeDSStart
}: RunThreeDSecureFlowArgs<T>): Promise<T> {
  const eventInfo = { ...analyticsInfo, checkout_session_id: checkoutSessionId };

  logThreeDSecureActionRequired(eventInfo);
  onThreeDSStart();

  try {
    let resultTokenId: string;
    try {
      resultTokenId = await runThreeDSecureChallenge({ actionTokenId, container });
    } catch (error) {
      logThreeDSecureChallengeFailed({
        ...eventInfo,
        ...(isRecurlyError(error)
          ? { error_code: error.code, failure_reason: error.message }
          : { failure_reason: error instanceof Error ? error.message : undefined })
      });
      throw error;
    }
    logThreeDSecureAuthCompleted(eventInfo);
    return await completeCheckoutSession(resultTokenId);
  } finally {
    onThreeDSEnd();
  }
}
